import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, Activity, Stethoscope, Truck, Languages, Ear, Eye, Pill, Clock, ShieldCheck, TrendingDown, Users, BoxIcon } from 'lucide-react';
const services = [{
  icon: Activity,
  name: 'Physical & Occupational Therapy',
  description: 'Coordinated PT/OT scheduling within 24 hours, with progress tracking and outcomes-based provider selection.'
}, {
  icon: Stethoscope,
  name: 'Diagnostic Imaging',
  description: 'MRI, CT, X-ray, and EMG/NCV scheduling at preferred-rate facilities nationwide.'
}, {
  icon: BoxIcon,
  name: 'Durable Medical Equipment',
  description: 'Same-day DME delivery for braces, mobility aids, TENS units, and home recovery equipment.'
}, {
  icon: Truck,
  name: 'Transportation Services',
  description: 'Reliable medical transportation to and from appointments, including specialty vehicles when needed.'
}, {
  icon: Languages,
  name: 'Translation & Interpretation',
  description: 'On-demand interpretation in 200+ languages, both telephonic and on-site.'
}, {
  icon: Pill,
  name: 'Pharmacy Coordination',
  description: 'Streamlined first-fill, ongoing prescription management, and medication adherence support.'
}, {
  icon: Ear,
  name: 'Audiology Services',
  description: 'Hearing evaluations, hearing aids, and tinnitus assessments through a vetted national network.'
}, {
  icon: Eye,
  name: 'Specialty Services',
  description: 'Dental, vision, home health, and other ancillary care coordinated through a single point of contact.'
}];
const benefits = [{
  icon: Clock,
  title: 'Faster scheduling',
  stat: '24hr',
  description: 'Average appointment scheduling turnaround — getting injured workers into care without delay.'
}, {
  icon: TrendingDown,
  title: 'Lower medical spend',
  stat: '32%',
  description: 'Average savings on ancillary spend through negotiated rates and outcomes-based provider selection.'
}, {
  icon: Users,
  title: 'One coordinator',
  stat: '1:1',
  description: 'A single dedicated coordinator owns the case end-to-end — no handoffs, no lost details.'
}, {
  icon: ShieldCheck,
  title: 'Quality assurance',
  stat: '100%',
  description: 'Every provider in our network is credentialed, monitored, and outcomes-tracked.'
}];
export function AncillaryServices() {
  return <div className="bg-brand-paper" data-id="element-436">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-437">
        <div className="absolute inset-0 opacity-10" data-id="element-438">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-439"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-440"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-441">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-442">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-443">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-444">
                Featured Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-445">
              A+ <span className="text-brand-gold italic" data-id="element-446">Ancillary</span>{' '}
              Services
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-447">
              A single, integrated solution for every ancillary need in your
              workers' compensation program — from physical therapy and
              diagnostics to DME, transportation, and beyond.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-448">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-449">
                Request a Demo <ArrowRight className="w-5 h-5 ml-2" data-id="element-450" />
              </Link>
              <a href="tel:8552601053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-451">
                <Phone className="w-5 h-5 mr-2" data-id="element-452" /> 855.260.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro / Why Ancillary */}
      <section className="py-24 bg-white" data-id="element-453">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-454">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-455">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform rotate-2" data-id="element-456"></div>
            <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Physical therapist helping a patient with recovery exercises" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-457" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-458">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-459">
              The Problem We Solve
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-460">
              Fragmented ancillary care is costing you — and your injured
              workers.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-461">
              When PT, diagnostics, DME, and transportation are coordinated
              across multiple vendors, claims slow down. Injured workers wait
              days for appointments. Adjusters chase paperwork. Costs creep up.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-462">
              A+ Ancillary Services consolidates every ancillary need under one
              roof, one coordinator, and one transparent reporting dashboard —
              so care moves at the speed of recovery, not paperwork.
            </p>
            <ul className="space-y-4" data-id="element-463">
              {['One single point of contact per claim', 'One integrated invoice — no vendor reconciliation', 'One real-time dashboard for every stakeholder', 'One performance guarantee on outcomes and savings'].map(item => <li key={item} className="flex items-start" data-id="element-464">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-465" />
                  <span className="text-gray-800 font-medium" data-id="element-466">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Service Catalog */}
      <section className="py-24 bg-brand-paper" data-id="element-467">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-468">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-469">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-470">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-471">
              Every ancillary need, covered.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-472">
              From the first day of injury through return to work, we coordinate
              the full spectrum of ancillary services.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-473">
            {services.map((svc, i) => {
            const Icon = svc.icon;
            return <motion.div key={svc.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-474">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-475">
                    <Icon className="w-6 h-6" data-id="element-476" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-477">
                    {svc.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-478">
                    {svc.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits / Stats */}
      <section className="py-24 bg-white" data-id="element-479">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-480">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-481">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-482">
              The A+ Difference
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-483">
              Measurable outcomes from day one.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-484">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-485">
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-486"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-487" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-488">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-489">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-490">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-brand-paper" data-id="element-491">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-492">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-493">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-494">
              How It Works
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-495">
              Simple referral. Seamless coordination.
            </h3>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-496">
            <div className="hidden md:block absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-gray-200" data-id="element-497"></div>
            {[{
            step: '01',
            title: 'Submit referral',
            desc: 'One online form or fax sends your referral straight to our intake team.'
          }, {
            step: '02',
            title: 'We coordinate',
            desc: 'A dedicated coordinator schedules every service and verifies authorization.'
          }, {
            step: '03',
            title: 'Care delivered',
            desc: 'Injured workers receive timely, high-quality care from credentialed providers.'
          }, {
            step: '04',
            title: 'Transparent reporting',
            desc: 'Real-time updates and detailed outcomes reporting for every stakeholder.'
          }].map((s, i) => <motion.div key={s.step} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative text-center" data-id="element-498">
                <div className="w-24 h-24 mx-auto bg-white border-4 border-brand-gold rounded-full flex items-center justify-center mb-6 relative z-10 shadow-md" data-id="element-499">
                  <span className="font-serif font-bold text-2xl text-brand-navy" data-id="element-500">
                    {s.step}
                  </span>
                </div>
                <h4 className="font-serif font-bold text-xl text-brand-navy mb-3" data-id="element-501">
                  {s.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-502">
                  {s.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-503">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-504"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-505">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-506">
            See A+ Ancillary in action.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-507">
            Schedule a 30-minute walkthrough with our team. We'll show you
            exactly how A+ Ancillary Services can drive savings and better
            outcomes for your program.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-508">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-509">
              Schedule a Walkthrough
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-510">
              <Phone className="w-5 h-5 mr-2" data-id="element-511" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}